import { CalendarHeart, Gauge, Inbox, ChartLine, Plane, Users } from "lucide-react";
import { Suspense, lazy } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { defineFeature } from "../types";

// React.lazy so the settings page costs the entry bundle nothing but its icon.
const CapacitySettingsPage = lazy(() => import("./settings"));

/**
 * Workload — team capacity, leave, bookings, utilisation and the pipeline
 * demand forecast (PRD §8 Item 12, US-12.1 … US-12.6, plus US-10.8).
 *
 * It owns no records of its own except leave and bookings: the grid is built
 * from tasks, project allocations and time, read through those modules' public
 * seams. That is why its query keys start with `task` and `member` rather than
 * inventing a type of their own.
 */
export default defineFeature({
  key: "workload",
  title: "Workload",
  routes: [
    { path: "workload", lazy: () => import("./page") },
    { path: "workload/forecast", lazy: () => import("./forecast") },
    { path: "leave", lazy: () => import("./leave") },
  ],
  nav: [
    {
      label: "Workload",
      to: "/workload",
      icon: Gauge,
      group: "delivery",
      order: 45,
      permission: "projects:read",
      description: "Who is free, who is drowning, and what nobody owns",
    },
    {
      label: "Leave",
      to: "/leave",
      icon: Plane,
      group: "workspace",
      order: 25,
      description: "Time off and capacity bookings",
    },
  ],
  quickCreate: [
    {
      key: "leave",
      label: "Time off",
      icon: Plane,
      to: "/leave?new=1",
      description: "Request leave for yourself or the team",
    },
  ],
  commands: [
    { key: "workload.grid", label: "Team workload", icon: Users, to: "/workload", group: "navigate", permission: "projects:read", keywords: ["capacity", "overbooked", "availability"] },
    { key: "workload.unassigned", label: "Unassigned work", icon: Inbox, to: "/workload", group: "actions", permission: "projects:read", keywords: ["nobody", "pool", "tray"] },
    { key: "workload.forecast", label: "Demand forecast", icon: ChartLine, to: "/workload/forecast", group: "navigate", permission: "reports:view", keywords: ["hiring", "bench", "pipeline", "fte"] },
    { key: "workload.leave", label: "Leave requests", icon: Plane, to: "/leave", group: "navigate", keywords: ["holiday", "time off", "absence"] },
    { key: "workload.bookings", label: "Capacity bookings", icon: CalendarHeart, to: "/leave?tab=bookings", group: "navigate", permission: "projects:write", keywords: ["resourcing", "soft book"] },
  ],
  settingsSections: [
    {
      key: "capacity",
      label: "Capacity",
      path: "capacity",
      group: "Delivery",
      order: 135,
      icon: Gauge,
      description: "The working week, the colour bands and the forecast's conversion rates",
      permission: "projects:read",
      element: (
        <Suspense fallback={<Skeleton className="h-72 w-full" rounded="card" />}>
          <CapacitySettingsPage />
        </Suspense>
      ),
    },
  ],
});
